/**
 * @module components/TabsTableHazard
 * @author Charles Blais <charles.blais@canada.ca>
 * 
 * Tab display of various hazard table displays
 */

import { useMemo } from 'react';

import { Tabs, Tab } from 'react-bootstrap';

import { useLanguageState } from '../contexts/language-state-provider';

import RequestSummary from './request-summary';
import TabsTableModel from './tabs-table-model';
import TabsTableEngineer from './tabs-table-engineer';
import TabsChartHazard from './tabs-chart-hazard';
import TabsApi from './tabs-api';
import TabsBackground from './tabs-background';

type Props = {
  variables: HazardParameters
  data: SiteDesignationsXVResponse | SiteDesignationsXSResponse,
}


/**
 * React component
 * ===============
 */
const TabsHazard = ({
  variables,
  data,
}: Props) => {
  const [ lang ] = useLanguageState();

  // Memorize data to speed up re-rendering
  const mdata = useMemo(() => data, [data]);

  // Convert code/model into a print friendly format
  const code = lang.codeMap[variables.code];
  const model = lang.modelMap[variables.code];

  // There are two types of displays depending if the requested
  // is "nbc" or "canSHM".
  if ( /^nbc/i.test(variables.code) ) {

    return (
      <>
        <h2>{lang.title}</h2>
        <RequestSummary
          variables={variables}
        />
        {lang.tab.intro}
        <Tabs defaultActiveKey={1} id="tabHazard" unmountOnExit={true}>
          <Tab eventKey={1} title={code}>
            <TabsTableEngineer
              variables={variables}
              data={mdata} />
          </Tab>
          <Tab eventKey={2} title={lang.tab.additional}>
            <TabsTableModel
              variables={variables}
              data={mdata} />
          </Tab>
          <Tab eventKey={3} title={lang.tab.plots}>
            <TabsChartHazard
              variables={variables}
              data={mdata}
            />
          </Tab>
          <Tab eventKey={4} title={lang.tab.api}>
            <TabsApi />
          </Tab>
          <Tab eventKey={5} title={lang.tab.information}>
            <TabsBackground variables={variables}/>
          </Tab>
        </Tabs>
      </>
    );
  
  }

  return (
    <>
      <h2>{lang.title}</h2>
      <RequestSummary
        variables={variables}
      />
      {lang.tab.intro}
      <Tabs defaultActiveKey={1} id="tabHazard" unmountOnExit={true}>
        <Tab eventKey={1} title={model}>
          <TabsTableModel
            variables={variables}
            data={mdata} />
        </Tab>
        <Tab eventKey={2} title={lang.tab.plots}>
          <TabsChartHazard
            variables={variables}
            data={mdata}
          />
        </Tab>
        <Tab eventKey={3} title={lang.tab.api}>
          <TabsApi />
        </Tab>
        <Tab eventKey={4} title={lang.tab.information}>
          <TabsBackground variables={variables}/>
        </Tab>
      </Tabs>
    </>
  );
};

export default TabsHazard;